import React, { Fragment } from 'react';
import _ from 'lodash';
import {
    Card,
    CardBody,
    CardHeader,
    Collapse,
    FormGroup,
    Input,
    Label
} from 'reactstrap';
import qs from 'qs';
import { withRouter } from 'react-router-dom';

import { urlify } from '../../utils';
import CollapseButton from '../dumb/collapseButton';
import baseFilter from './baseFilter';


class MalignancyFilter extends baseFilter {

    constructor(props) {
        super(props);

        this.toggleCheckbox = this.toggleCheckbox.bind(this);
        this.toggleCollapse = this.toggleCollapse.bind(this);
        this.processResponse = this.processResponse.bind(this);
        this.updateAPIURL = this.updateAPIURL.bind(this);

        const current_qs = qs.parse(
            window.location.search,
            { ignoreQueryPrefix: true }
        );

        // Prior malignancy categories aren't a fixed, known set (unlike
        // alcohol status), so filters are discovered dynamically from the
        // aggregate response as they come back, rather than pre-declared.
        this.state = {
            isOpen: false,
            filters: MalignancyFilter.getFiltersFromQS(current_qs, {}),
            activeCount: 0
        };

        this.aggregates = {
            'sample.patient.prior_malignancies': 'count'
        };
    }


    /*
     * Get the selected malignancy filters from the URL
     */
    static getFiltersFromQS(qsObj, oldfilters) {

        const qsMalignancyFilter =
            ('malignancy' in qsObj)
                ? _.split(qsObj.malignancy, '_')
                : [];

        return _.reduce(
            oldfilters,
            (res, filterProps, malignancy) => {

                res[malignancy] = {
                    ...filterProps,

                    active:
                        _.indexOf(
                            qsMalignancyFilter,
                            urlify(malignancy)
                        ) >= 0
                };

                return res;
            },
            {}
        );
    }


    static getDerivedStateFromProps(newprops, oldstate) {

        const current_qs = qs.parse(
            newprops.location.search,
            { ignoreQueryPrefix: true }
        );

        const newfilters = MalignancyFilter.getFiltersFromQS(
            current_qs,
            oldstate.filters
        );

        return {
            filters: newfilters,
            activeCount: _.filter(newfilters, 'active').length
        };
    }


    /*
     * Add the selected malignancy filters to the API request.
     */
    updateAPIURL(current_api_qs, newlistprops) {

        const new_qs = qs.parse(
            newlistprops.location.search,
            { ignoreQueryPrefix: true }
        );

        const qsMalignancyFilter = MalignancyFilter.getFiltersFromQS(
            new_qs,
            this.state.filters
        );

        const apiMalignancyFilters = _.map(
            _.filter(
                _.toPairs(qsMalignancyFilter),
                ([malignancy, filterProps]) => filterProps.active
            ),
            ([malignancy, filterProps]) => filterProps.filter_name
        );

        const filter_qs = {
            name: "sample",
            op: "has",
            val: {
                name: "patient",
                op: "has",
                val: {
                    name: "prior_malignancies",
                    op: "in",
                    val: apiMalignancyFilters
                }
            }
        };

        if (apiMalignancyFilters.length > 0) {
            current_api_qs.filter = [
                ...(current_api_qs.filter || []),
                filter_qs
            ];
        }

        return _.merge(current_api_qs, { agg: this.aggregates });
    }


    /*
     * Convert the aggregate response into the values displayed next
     * to each checkbox. Malignancy categories aren't fixed up front,
     * so unseen values become new filter entries (dropping the "None"
     * bucket, same as before); known ones just get their count and
     * filter_name refreshed, preserving `active` from current state.
     */
    processResponse(response) {

        const resp_agg = this.get_agg_values(response);
        const agg_field = _.keys(this.aggregates)[0];

        if (!resp_agg) {
            return;
        }

        const counts = _.get(
            resp_agg,
            [agg_field, this.aggregates[agg_field]],
            {}
        );

        const newfilters = _.reduce(
            counts,
            (res, count, malignancy) => {

                if (malignancy === 'None') {
                    return res;
                }

                res[malignancy] = {
                    ...(this.state.filters[malignancy] || { active: false }),
                    filter_name: malignancy,
                    count: Number(count) || 0
                };

                return res;
            },
            {}
        );

        this.setState({ filters: newfilters });
    }


    toggleCheckbox(event) {

        const malignancy = urlify(event.target.name);

        const current_qs = qs.parse(
            window.location.search,
            { ignoreQueryPrefix: true }
        );

        let malignancyFilter =
            ('malignancy' in current_qs)
                ? _.split(current_qs.malignancy, '_')
                : [];

        if (event.target.checked) {
            if (!malignancyFilter.includes(malignancy)) {
                malignancyFilter.push(malignancy);
            }
        } else {
            malignancyFilter = malignancyFilter.filter(
                (item) => urlify(item) !== malignancy
            );
        }

        current_qs.malignancy =
            malignancyFilter.length > 0
                ? _.join(malignancyFilter.sort(), '_')
                : undefined;

        current_qs.page = undefined;

        this.props.history.push(
            window.location.pathname +
            qs.stringify(current_qs, { addQueryPrefix: true })
        );
    }


    toggleCollapse() {
        this.setState({ isOpen: !this.state.isOpen });
    }


    renderMalignancyCheckboxes() {

        if (_.isUndefined(this.state.filters)) {
            return null;
        }

        let active = [];
        let inactive = [];

        _.map(
            _.toPairs(this.state.filters),
            ([key, filterProps]) => {

                const count = Number(filterProps.count) || 0;

                const cb = (
                    <FormGroup check key={key}>
                        <Input
                            type="checkbox"
                            name={key}
                            onChange={this.toggleCheckbox}
                            checked={!!filterProps.active}
                        />
                        {' '}
                        <Label className="ml-4 mt-1" check>
                            {key}
                            <span className="secondary">
                                {' '}({count})
                            </span>
                        </Label>
                    </FormGroup>
                );

                if (filterProps.active) {
                    active.push(cb);
                } else {
                    inactive.push(cb);
                }
            }
        );

        // Show active filters first, followed by enough inactive
        // filters to make five visible.
        if (active.length < 5 && active.length + inactive.length > 0) {
            active = active.concat(
                inactive.splice(0, 5 - active.length)
            );
        }

        return (
            <Fragment>
                {active}
                {inactive}
            </Fragment>
        );
    }


    componentDidMount() {
        this.props.registerResponse(this.processResponse);
        this.props.registerRequest(this.updateAPIURL);
    }


    componentWillUnmount() {
        this.props.unregisterResponse(this.processResponse);
        this.props.unregisterRequest(this.updateAPIURL);
    }


    render() {
        return (
            <Card className="malignancy-filters mb-2 z0 hover">
                <CardHeader
                    className="justify-content-between d-flex"
                    onClick={this.toggleCollapse}
                >
                    <div className="d-inline w-50">Prior Malignancies</div>
                    <div className="d-inline-block">
                        {this.state.activeCount > 0 && (
                            <strong>{this.state.activeCount + ' active'}</strong>
                        )}
                    </div>
                    <CollapseButton
                        isOpen={this.state.isOpen}
                        onClick={this.toggleCollapse}
                    />
                </CardHeader>
                <Collapse isOpen={this.state.isOpen}>
                    <CardBody>
                        {this.renderMalignancyCheckboxes()}
                    </CardBody>
                </Collapse>
            </Card>
        );
    }
}


export default withRouter(MalignancyFilter);